import { Button, Card, Center, Group, Loader, ScrollArea, Select, Stack, Title } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import type { ColDef, GetRowIdFunc, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import { GetUsageAggregateProjectStartsApiResponse, useGetUsageAggregateProjectStartsQuery } from "../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { FailedAlert } from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import ProjectUsageModal from "./ProjectUsageModal";
import { setAppName, setFromDate, setToDate } from "./statsSlice";

const APP_OPTIONS = [
  { label: "Møter", value: MCApplicationsUrlEnum.MEETING },
  { label: "Rapporter", value: MCApplicationsUrlEnum.REPORT },
  { label: "Prosjektmedlemmer", value: MCApplicationsUrlEnum.MEMBERS },
  { label: "Register", value: MCApplicationsUrlEnum.ISSUE },
  { label: "Sjekkliste", value: MCApplicationsUrlEnum.CHECKLIST },
] as const;

const ProjectUsageTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const appName = useAppSelector((s) => s.statsReducer.appName);
  const fromDate = useAppSelector((s) => s.statsReducer.fromDate);
  const toDate = useAppSelector((s) => s.statsReducer.toDate);

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const validRange = !fromDate || !toDate || toDate >= fromDate;
  const { data, isFetching, isError } = useGetUsageAggregateProjectStartsQuery(
    {
      fromDate: fromDate,
      toDate: toDate,
      appName: appName || undefined,
    },
    { skip: !validRange },
  );

  // Row = en enskild rad i svaret
  type Row = GetUsageAggregateProjectStartsApiResponse[number];

  const columns = useMemo<ColDef<Row>[]>(
    () => [
      {
        field: "projectName",
        sortable: true,
        filter: "agTextColumnFilter",
        flex: 1,
        cellRenderer: ProjectLinkCell,
        cellRendererParams: { onOpen: (id: string) => setSelectedProjectId(id) },
      },
      {
        field: "count",
        initialSort: "desc",
        valueGetter: (p) => p.data.count,
        width: 120,
      },
    ],
    [],
  );

  const defaultColDef: ColDef = { suppressHeaderMenuButton: true, cellClass: "d-block" };
  const getRowId: GetRowIdFunc = (p) => p.data.projectId;

  return (
    <>
      {isError && <FailedAlert />}
      <Card withBorder p="lg" radius="lg" mt="md" mx="md">
        <Stack gap="md">
          <Title order={3}>Antall appstarter per prosjekt</Title>

          <Group align="end" gap="lg" wrap="wrap">
            <DateInput
              label="Fra"
              value={fromDate}
              onChange={(d) => dispatch(setFromDate(d))}
              clearable
              maxDate={toDate ?? undefined}
              valueFormat="YYYY-MM-DD"
              placeholder="Velg dato"
              maw={240}
              w="100%"
            />
            <DateInput
              label="Til"
              value={toDate}
              onChange={(d) => dispatch(setToDate(d))}
              clearable
              minDate={fromDate ?? undefined}
              valueFormat="YYYY-MM-DD"
              placeholder="Velg dato"
              maw={240}
              w="100%"
            />
            <Select
              label="App"
              data={APP_OPTIONS}
              value={appName || null}
              onChange={(v) => dispatch(setAppName(v ?? ""))}
              clearable
              searchable
              placeholder="Alle"
              maw={280}
              w="100%"
            />
          </Group>

          <ScrollArea h="50vh" type="auto" scrollHideDelay={500}>
            <div className="ag-theme-quartz" style={{ minHeight: 200, opacity: isFetching ? 0.6 : 1 }}>
              {data ? (
                <AgGridReact
                  domLayout="autoHeight"
                  rowData={data}
                  getRowId={getRowId}
                  defaultColDef={defaultColDef}
                  headerHeight={35}
                  columnDefs={columns}
                  suppressClickEdit
                  animateRows={false}
                  suppressContextMenu
                  localeText={AG_GRID_LOCALE_NOR}
                  pagination
                  paginationPageSize={50}
                />
              ) : (
                <Center py="xl">
                  <Loader />
                </Center>
              )}
            </div>
          </ScrollArea>
        </Stack>
      </Card>

      {selectedProjectId != null && (
        <ProjectUsageModal projectId={selectedProjectId} onClose={() => setSelectedProjectId(null)} />
      )}
    </>
  );
};

export default ProjectUsageTable;

const ProjectLinkCell: React.FC<ICellRendererParams & { onOpen: (projectId: string) => void }> = React.memo(
  (params) => {
    const id = params.data?.projectId as string;
    const label = String(params.value ?? "");
    return (
      <Button
        variant="subtle"
        size="compact-sm"
        onClick={() => params.onOpen(id)}
        styles={{ root: { paddingLeft: 0, paddingRight: 0 } }}
      >
        {label}
      </Button>
    );
  },
);
ProjectLinkCell.displayName = "ProjectLinkCell";
